import { defineMessages, IntlShape } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { StudioActionFeedback, studioActionErrorFeedback, studioErrorCode } from '../studio/studioActionErrorText';

/** The `cause.code` of music24-api when the AI cannot write captions now (503). */
export const AI_CAPTIONS_UNAVAILABLE_CODE = 'M24_AI_CAPTIONS_UNAVAILABLE';

export const composerAiErrorMessages = defineMessages({
  unavailable: {
    id: 'app.sml.promote.composer.ai.unavailable',
    defaultMessage: 'AI writing is not available right now. Write the post yourself.',
  },
});

/**
 * True when the AI could not write for a reason on our side: the 503 `M24_AI_CAPTIONS_UNAVAILABLE`,
 * an older server that answers 500 for it, or any other 5xx. The user cannot fix it, so the
 * Composer says that AI writing is not available and that the user can write the post. Pure.
 */
export function isAiWritingUnavailable(error: unknown): boolean {
  if (studioErrorCode(error) === AI_CAPTIONS_UNAVAILABLE_CODE) {
    return true;
  }
  return error instanceof FYMAPIError && error.statusCode >= 500;
}

/**
 * The sentence next to the AI buttons after "Write with AI" or "Tailor per platform" failed (QA
 * finding B12). An outage gets the plain "not available" sentence. A refusal with an `M24_*` code
 * (the spent AI budget of a Free plan) keeps the sentence of the server. Every other error gets
 * the general Studio sentence; the raw text goes only to the console and Sentry.
 */
export function composerAiErrorFeedback(intl: IntlShape, error: unknown): StudioActionFeedback {
  if (isAiWritingUnavailable(error)) {
    console.warn('Composer: AI writing is not available', error);
    return { tone: 'info', message: intl.formatMessage(composerAiErrorMessages.unavailable) };
  }
  return studioActionErrorFeedback(intl, 'composerCaptions', error);
}
